// Versão: 1.03
// Data..: 2026-10-15
// Geração de relatório gerencial em PDF (client-side, sem armazenar). Desenha o layout
// com jsPDF (cabeçalho, KPIs e tabelas) para um resultado limpo e imprimível.
// v1.03: generateSupportHoursPdf detalha, por empresa, os CHAMADOS ATENDIDOS que
// geraram as horas (número, assunto, data, responsável, horas) após a tabela de totais.
// v1.02: generateSupportHoursPdf — PDF do relatório "Horas de suporte por empresa"
// (colunas em HH:MM + valor adicional, com linha de totais).
// v1.01: inclui a tabela "Chamados por empresa".

import { jsPDF } from 'jspdf';
import type { ManagementMetrics } from '@makweb/shared';

// Paleta alinhada à identidade do portal.
const BRAND: [number, number, number] = [0, 136, 204];
const DARK: [number, number, number] = [15, 23, 42];
const MUTED: [number, number, number] = [100, 116, 139];
const LIGHT: [number, number, number] = [241, 245, 249];

interface ReportInput {
  metrics: ManagementMetrics;
  statusData: { label: string; value: number }[];
  priorityData: { label: string; value: number }[];
  fmtMin: (m: number) => string;
}

// Formata minutos como texto simples (reaproveita o formatador da tela).
export function generateManagementPdf({ metrics: m, statusData, priorityData, fmtMin }: ReportInput): void {
  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const margin = 40;
  let y = margin;

  // ---- Cabeçalho ----
  doc.setFillColor(...BRAND);
  doc.rect(0, 0, pageW, 6, 'F');
  doc.setTextColor(...DARK);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(20);
  doc.text('Relatório Gerencial', margin, (y += 24));
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(...MUTED);
  doc.text('Makweb Soluções — Portal de Chamados', margin, (y += 16));
  const now = new Date().toLocaleString('pt-BR');
  doc.text(`Emitido em ${now}`, margin, (y += 14));
  y += 10;

  // ---- KPIs (grade de cartões) ----
  const kpis: { label: string; value: string }[] = [
    { label: 'Chamados ativos', value: String(m.totalOpen) },
    { label: 'Encerrados', value: String(m.totalResolved) },
    { label: 'SLA cumprido', value: `${m.slaResolutionRate}%` },
    { label: 'Média 1ª resposta', value: fmtMin(m.avgFirstResponseMinutes) },
    { label: 'Média resolução', value: fmtMin(m.avgResolutionMinutes) },
    { label: 'Dentro/Fora do prazo', value: `${m.slaResolutionMet} / ${m.slaResolutionBreached}` },
  ];
  const cols = 3;
  const gap = 12;
  const cardW = (pageW - margin * 2 - gap * (cols - 1)) / cols;
  const cardH = 56;
  kpis.forEach((k, i) => {
    const col = i % cols;
    const row = Math.floor(i / cols);
    const x = margin + col * (cardW + gap);
    const cy = y + row * (cardH + gap);
    doc.setFillColor(...LIGHT);
    doc.roundedRect(x, cy, cardW, cardH, 6, 6, 'F');
    doc.setTextColor(...MUTED);
    doc.setFontSize(8.5);
    doc.setFont('helvetica', 'normal');
    doc.text(k.label.toUpperCase(), x + 12, cy + 18);
    doc.setTextColor(...DARK);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(18);
    doc.text(k.value, x + 12, cy + 42);
  });
  y += Math.ceil(kpis.length / cols) * (cardH + gap) + 8;

  // ---- Tabelas ----
  const drawTable = (title: string, rows: { label: string; value: number | string }[]) => {
    if (y > doc.internal.pageSize.getHeight() - 120) { doc.addPage(); y = margin; }
    doc.setTextColor(...BRAND);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.text(title, margin, (y += 18));
    y += 6;
    if (rows.length === 0) {
      doc.setTextColor(...MUTED);
      doc.setFont('helvetica', 'italic');
      doc.setFontSize(9);
      doc.text('Sem dados no período.', margin, (y += 14));
      y += 6;
      return;
    }
    const rowH = 20;
    rows.forEach((r, i) => {
      if (y > doc.internal.pageSize.getHeight() - 60) { doc.addPage(); y = margin; }
      if (i % 2 === 0) {
        doc.setFillColor(248, 250, 252);
        doc.rect(margin, y, pageW - margin * 2, rowH, 'F');
      }
      doc.setTextColor(...DARK);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      doc.text(String(r.label), margin + 8, y + 14);
      doc.setFont('helvetica', 'bold');
      doc.text(String(r.value), pageW - margin - 8, y + 14, { align: 'right' });
      y += rowH;
    });
    y += 8;
  };

  drawTable('Chamados por status', statusData);
  drawTable('Chamados por prioridade', priorityData);
  drawTable('Chamados por empresa', m.ticketsByCompany);
  drawTable('Backlog por categoria', m.backlogByCategory);
  drawTable('Ranking de técnicos (encerrados)', m.technicianRanking);
  drawTable('Horas por empresa', m.hoursByCompany.map((h) => ({ label: h.label, value: `${h.value} h` })));

  // ---- Rodapé em todas as páginas ----
  const pages = doc.getNumberOfPages();
  for (let p = 1; p <= pages; p++) {
    doc.setPage(p);
    doc.setTextColor(...MUTED);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.text(`Página ${p} de ${pages}`, pageW - margin, doc.internal.pageSize.getHeight() - 20, { align: 'right' });
    doc.text('Makweb Soluções — confidencial', margin, doc.internal.pageSize.getHeight() - 20);
  }

  const stamp = new Date().toISOString().slice(0, 10);
  doc.save(`relatorio-gerencial-${stamp}.pdf`);
}

// Linha do relatório de horas de suporte por empresa (espelha BillingCompanyRow do shared,
// com só o que o PDF precisa).
export interface SupportHoursPdfRow {
  companyName: string;
  contractedMinutes: number;
  billableMinutes: number;
  overageMinutes: number;
  overageAmount?: number;
  // Chamados atendidos no período (um por apontamento), para o detalhamento do PDF.
  tickets?: {
    ticketNumber?: number;
    subject?: string;
    performedAt: string; // ISO
    userName?: string;
    minutes: number;
    billable: boolean;
    description?: string;
  }[];
}

export interface SupportHoursPdfInput {
  rows: SupportHoursPdfRow[];
  from: string; // YYYY-MM-DD
  to: string;   // YYYY-MM-DD
}

// Formata minutos como "HH:MM" (zero-padded). Ex.: 750 min -> "12:30".
function fmtHoursMinutes(min: number): string {
  const total = Math.max(0, Math.round(min));
  const h = Math.floor(total / 60);
  const m = total % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

// Formata um valor em R$ (pt-BR). undefined/null -> "—".
function fmtBRL(v?: number): string {
  return v == null ? '—' : v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

// Formata uma data ISO como "dd/mm/aaaa". Entrada inválida -> "—".
function fmtDateIso(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  return `${dd}/${mm}/${d.getFullYear()}`;
}

// Trunca um texto a um limite de caracteres, adicionando reticências.
function truncate(text: string, max: number): string {
  return text.length > max ? `${text.slice(0, max - 1)}…` : text;
}

// Gera o PDF do relatório "Horas de suporte por empresa (contrato)" (client-side).
// Colunas: Empresa, Horas contratadas, Horas gastas, Excedente (todas em HH:MM) e
// Valor adicional (R$). Linhas com excedente > 0 são destacadas.
export function generateSupportHoursPdf({ rows, from, to }: SupportHoursPdfInput): void {
  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 40;
  let y = margin;

  // Cabeçalho.
  doc.setFillColor(...BRAND);
  doc.rect(0, 0, pageW, 6, 'F');
  doc.setTextColor(...DARK);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(18);
  doc.text('Horas de Suporte por Empresa', margin, (y += 24));
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(...MUTED);
  doc.text('Makweb Soluções — Portal de Chamados', margin, (y += 16));
  const fmtDate = (d: string) => { const [yy, mm, dd] = d.split('-'); return `${dd}/${mm}/${yy}`; };
  doc.text(`Período: ${fmtDate(from)} a ${fmtDate(to)}`, margin, (y += 14));
  doc.text(`Emitido em ${new Date().toLocaleString('pt-BR')}`, margin, (y += 14));
  y += 12;

  // Colunas (x = início de cada coluna). A 1ª alinhada à esquerda; as demais à direita.
  const colEmpresa = margin + 6;
  const colContr = margin + 250;
  const colGasto = margin + 345;
  const colExced = margin + 435;
  const colValor = pageW - margin - 6;

  // Cabeçalho da tabela.
  const drawHeader = () => {
    doc.setFillColor(...BRAND);
    doc.rect(margin, y, pageW - margin * 2, 22, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.text('Empresa', colEmpresa, y + 15);
    doc.text('Contratadas', colContr, y + 15, { align: 'right' });
    doc.text('Gastas', colGasto, y + 15, { align: 'right' });
    doc.text('Excedente', colExced, y + 15, { align: 'right' });
    doc.text('Valor adic.', colValor, y + 15, { align: 'right' });
    y += 22;
  };

  if (rows.length === 0) {
    drawHeader();
    doc.setTextColor(...MUTED);
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(10);
    doc.text('Sem horas de suporte no período.', colEmpresa, (y += 20));
  } else {
    drawHeader();
    const rowH = 20;
    rows.forEach((r, i) => {
      if (y > pageH - 60) { doc.addPage(); y = margin; drawHeader(); }
      const over = r.overageMinutes > 0;
      // Fundo: destaque suave para excedente; zebra nas demais.
      if (over) { doc.setFillColor(254, 243, 199); doc.rect(margin, y, pageW - margin * 2, rowH, 'F'); }
      else if (i % 2 === 0) { doc.setFillColor(248, 250, 252); doc.rect(margin, y, pageW - margin * 2, rowH, 'F'); }
      doc.setTextColor(...DARK);
      doc.setFont('helvetica', over ? 'bold' : 'normal');
      doc.setFontSize(9.5);
      // Trunca nomes muito longos para não invadir a coluna de números.
      const name = r.companyName.length > 42 ? `${r.companyName.slice(0, 41)}…` : r.companyName;
      doc.text(name, colEmpresa, y + 14);
      doc.setFont('helvetica', 'normal');
      doc.text(fmtHoursMinutes(r.contractedMinutes), colContr, y + 14, { align: 'right' });
      doc.text(fmtHoursMinutes(r.billableMinutes), colGasto, y + 14, { align: 'right' });
      doc.setFont('helvetica', over ? 'bold' : 'normal');
      doc.text(fmtHoursMinutes(r.overageMinutes), colExced, y + 14, { align: 'right' });
      doc.text(fmtBRL(r.overageAmount), colValor, y + 14, { align: 'right' });
      y += rowH;
    });

    // Totais (soma das colunas de minutos e do valor adicional).
    const totContr = rows.reduce((s, r) => s + r.contractedMinutes, 0);
    const totGasto = rows.reduce((s, r) => s + r.billableMinutes, 0);
    const totExced = rows.reduce((s, r) => s + r.overageMinutes, 0);
    const totValor = rows.reduce((s, r) => s + (r.overageAmount ?? 0), 0);
    if (y > pageH - 60) { doc.addPage(); y = margin; }
    doc.setDrawColor(...MUTED);
    doc.line(margin, y, pageW - margin, y);
    y += 4;
    doc.setTextColor(...DARK);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9.5);
    doc.text('TOTAL', colEmpresa, y + 14);
    doc.text(fmtHoursMinutes(totContr), colContr, y + 14, { align: 'right' });
    doc.text(fmtHoursMinutes(totGasto), colGasto, y + 14, { align: 'right' });
    doc.text(fmtHoursMinutes(totExced), colExced, y + 14, { align: 'right' });
    doc.text(fmtBRL(totValor), colValor, y + 14, { align: 'right' });
    y += rowH;
  }

  // ---- Detalhamento: chamados atendidos por empresa ----
  // Após os totais, uma subseção por empresa que tenha chamados, para apresentar ao
  // cliente cada intervenção (número, assunto, data, responsável e horas).
  drawTicketDetails(doc, rows, { pageW, pageH, margin });

  // Rodapé em todas as páginas.
  const pages = doc.getNumberOfPages();
  for (let p = 1; p <= pages; p++) {
    doc.setPage(p);
    doc.setTextColor(...MUTED);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.text(`Página ${p} de ${pages}`, pageW - margin, pageH - 20, { align: 'right' });
    doc.text('Makweb Soluções — confidencial', margin, pageH - 20);
  }

  doc.save(`horas-suporte-${from}_a_${to}.pdf`);
}

// Desenha as subseções "Chamados atendidos — <Empresa>" (uma por empresa com tickets),
// com os totais da empresa no topo e uma tabela por apontamento. Quebra de página com o
// mesmo padrão addPage/pageH do restante do relatório.
function drawTicketDetails(
  doc: jsPDF,
  rows: SupportHoursPdfRow[],
  layout: { pageW: number; pageH: number; margin: number },
): void {
  const { pageW, pageH, margin } = layout;
  const withTickets = rows.filter((r) => r.tickets && r.tickets.length > 0);
  if (withTickets.length === 0) return;

  // O detalhamento começa em página dedicada, para um layout limpo e previsível.
  doc.addPage();
  let y = margin;

  // Título da seção de detalhamento.
  doc.setFillColor(...BRAND);
  doc.rect(0, 0, pageW, 6, 'F');
  doc.setTextColor(...DARK);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.text('Chamados atendidos (detalhamento)', margin, (y += 24));
  y += 8;

  // Colunas da tabela de chamados.
  const colNum = margin + 6;
  const colSubject = margin + 70;
  const colDate = margin + 300;
  const colResp = margin + 370;
  const colHoras = pageW - margin - 6;

  const drawTicketHeader = () => {
    doc.setFillColor(...BRAND);
    doc.rect(margin, y, pageW - margin * 2, 20, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8.5);
    doc.text('Chamado', colNum, y + 14);
    doc.text('Assunto', colSubject, y + 14);
    doc.text('Data', colDate, y + 14);
    doc.text('Responsável', colResp, y + 14);
    doc.text('Horas', colHoras, y + 14, { align: 'right' });
    y += 20;
  };

  for (const row of withTickets) {
    const tickets = row.tickets!;

    // Precisamos de espaço para o título + totais + cabeçalho da tabela.
    if (y > pageH - 120) { doc.addPage(); y = margin; }

    // Subtítulo com o nome da empresa.
    doc.setTextColor(...BRAND);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.text(truncate(row.companyName, 60), margin, (y += 20));

    // Totais da empresa (contexto para o cliente).
    doc.setTextColor(...MUTED);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);
    const totais =
      `Contratadas: ${fmtHoursMinutes(row.contractedMinutes)}  •  ` +
      `Gastas: ${fmtHoursMinutes(row.billableMinutes)}  •  ` +
      `Excedente: ${fmtHoursMinutes(row.overageMinutes)}  •  ` +
      `Valor adic.: ${fmtBRL(row.overageAmount)}`;
    doc.text(totais, margin, (y += 14));
    y += 8;

    drawTicketHeader();

    const rowH = 18;
    tickets.forEach((t, i) => {
      // Estima a altura (linha + possível descrição) para decidir a quebra.
      const hasDesc = !!(t.description && t.description.trim());
      const needed = rowH + (hasDesc ? 12 : 0);
      if (y > pageH - 60 - needed) { doc.addPage(); y = margin; drawTicketHeader(); }

      if (i % 2 === 0) {
        doc.setFillColor(248, 250, 252);
        doc.rect(margin, y, pageW - margin * 2, rowH + (hasDesc ? 12 : 0), 'F');
      }

      doc.setTextColor(...DARK);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      const num = t.ticketNumber != null ? `#${t.ticketNumber}` : '—';
      doc.text(num, colNum, y + 13);
      const subject = truncate(t.subject || 'Sem assunto', 45);
      doc.text(subject, colSubject, y + 13);
      doc.text(fmtDateIso(t.performedAt), colDate, y + 13);
      doc.text(truncate(t.userName || '—', 16), colResp, y + 13);
      doc.setFont('helvetica', 'bold');
      // Marca apontamentos não faturáveis de forma discreta.
      const horas = t.billable ? fmtHoursMinutes(t.minutes) : `${fmtHoursMinutes(t.minutes)} (não fat.)`;
      doc.text(horas, colHoras, y + 13, { align: 'right' });

      y += rowH;

      // Linha secundária opcional com a descrição (truncada, cinza, fonte menor).
      if (hasDesc) {
        doc.setTextColor(...MUTED);
        doc.setFont('helvetica', 'italic');
        doc.setFontSize(8);
        doc.text(truncate(t.description!.trim(), 110), colSubject, y + 9);
        y += 12;
      }
    });

    y += 14;
  }
}
